import React, { useState, useEffect } from 'react';
import { generateClient } from 'aws-amplify/api';
import { MDBCard, MDBCardBody, MDBCardTitle, MDBRow, MDBCol, MDBTable, MDBTableHead, MDBTableBody, MDBSelect, MDBSpinner, MDBBtn, MDBIcon, MDBModal, MDBModalDialog, MDBModalContent, MDBModalHeader, MDBModalTitle, MDBModalBody } from 'mdb-react-ui-kit';
import * as XLSX from 'xlsx';
import { useTranslation } from 'react-i18next';
import { getLocalizedText } from '../../utils/localizedText';

const client = generateClient();

const EventEquipmentSummary = () => {
  const handleDownloadExcel = () => {
    // Daten für Excel vorbereiten
    const excelData = [];
    
    // Header hinzufügen
    excelData.push([
      "Kategorie",
      "Ausstattung",
      "Im Stand enthalten",
      "Zusätzlich gebucht",
      "Gesamt gebucht",
      "Aktueller Bestand"
    ]);

    // Daten hinzufügen
    equipmentSummary.forEach(item => {
      excelData.push([
        t(item.product.subType || item.product.type || '-'),
        getLocalizedText(item.product.name),
        item.includedQuantity,
        item.additionalQuantity,
        item.totalBooked,
        item.stock
      ]);
    });

    // Arbeitsmappe erstellen
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(excelData);

    // Spaltenbreiten automatisch anpassen
    ws['!cols'] = [
      { wch: 20 }, // Kategorie
      { wch: 40 }, // Ausstattung
      { wch: 20 }, // Im Stand enthalten
      { wch: 20 }, // Zusätzlich gebucht
      { wch: 15 }, // Gesamt gebucht
      { wch: 20 }  // Aktueller Bestand
    ];

    // Worksheet zur Arbeitsmappe hinzufügen
    XLSX.utils.book_append_sheet(wb, ws, 'Ausstattung');

    // Excel-Datei herunterladen
    XLSX.writeFile(wb, `Ausstattung-${getLocalizedText(events.find(e => e.id === selectedEvent)?.name)}.xlsx`);
  };
  const { t, i18n } = useTranslation();
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [equipmentSummary, setEquipmentSummary] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showModal, setShowModal] = useState(false);

  // Fetch events
  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const response = await client.graphql({
          query: `
            query ListEvents {
              listEvents(filter: { type: { eq: "fair" } }) {
                items {
                  id
                  name {
                    text
                    language
                  }
                  startDate
                  endDate
                  status
                  type
                }
              }
            }
          `
        });

        const activeEvents = response.data.listEvents.items.filter(
          event => event.status === "active" && new Date(event.endDate) >= new Date()
        );
        setEvents(activeEvents);
      } catch (error) {
        console.error('Error fetching events:', error);
      }
    };

    fetchEvents();
  }, []);

  // When an event is selected, fetch booths and equipment data
  useEffect(() => {
    if (!selectedEvent) return;

    const fetchEquipmentData = async () => {
      setIsLoading(true);
      try {
        // Fetch all booked booths with pagination
        const fetchAllBookedBooths = async () => {
          let allBookedBooths = [];
          let nextToken = null;

          do {
            const response = await client.graphql({
              query: `
                query SearchBookedBooths($filter: SearchableBookedBoothFilterInput, $limit: Int, $nextToken: String) {
                  searchBookedBooths(filter: $filter, limit: $limit, nextToken: $nextToken) {
                    items {
                      id
                      company {
                        id
                        name
                      }
                      orderLineItem {
                        order {
                          id
                          orderNumber
                        }
                      }
                      equipment {
                        items {
                          orderLineItem {
                            order {
                              id
                              orderNumber
                              status
                            }
                            product {
                              id
                              name {
                                language
                                text
                              }
                              type
                              subType
                            }
                            qty
                          }
                        }
                      }
                    }
                    nextToken
                  }
                }
              `,
              variables: {
                filter: {
                  eventBookedBoothsId: { eq: selectedEvent }
                },
                limit: 1000,
                nextToken
              }
            });

            allBookedBooths = [...allBookedBooths, ...response.data.searchBookedBooths.items];
            nextToken = response.data.searchBookedBooths.nextToken;
          } while (nextToken);

          return allBookedBooths;
        };

        const bookedBooths = await fetchAllBookedBooths();

        // Get all event products with pagination
        const fetchAllEventProducts = async () => {
          let allEventProducts = [];
          let nextToken = null;

          do {
            const response = await client.graphql({
              query: `
                query ListEventProducts($filter: ModelEventProductFilterInput, $limit: Int, $nextToken: String) {
                  listEventProducts(filter: $filter, limit: $limit, nextToken: $nextToken) {
                    items {
                      id
                      product {
                        id
                        name {
                          language
                          text
                        }
                        type
                        subType
                      }
                      stock
                    }
                    nextToken
                  }
                }
              `,
              variables: {
                filter: {
                  eventEventProductsId: { eq: selectedEvent }
                },
                limit: 1000,
                nextToken
              }
            });

            allEventProducts = [...allEventProducts, ...response.data.listEventProducts.items];
            nextToken = response.data.listEventProducts.nextToken;
          } while (nextToken);

          return allEventProducts;
        };

        const eventProducts = await fetchAllEventProducts();
        const productSummary = {};

        // Filter out booths and event spaces, add remaining products
        eventProducts.forEach(ep => {
          const productType = ep.product.type;
          if (productType === 'event_booth' || productType === 'event_space') {
            return; // Skip booths and event spaces
          }
          productSummary[ep.product.id] = {
            product: ep.product,
            includedQuantity: 0,
            additionalQuantity: 0,
            totalBooked: 0,
            stock: ep.stock || 0,
            bookings: [] // Details per company
          };
        });

        // Map bookings to products (add products from bookings that don't have EventProduct)
        bookedBooths.forEach(booth => {
          const mainOrderId = booth.orderLineItem.order.id;

          booth.equipment.items.forEach(equipment => {
            if (equipment.orderLineItem?.product) {
              const product = equipment.orderLineItem.product;
              const productId = product.id;

              // Skip booths and event spaces
              if (product.type === 'event_booth' || product.type === 'event_space') {
                return;
              }

              // Add product if it doesn't exist yet (from bookings without EventProduct)
              if (!productSummary[productId]) {
                productSummary[productId] = {
                  product: product,
                  includedQuantity: 0,
                  additionalQuantity: 0,
                  totalBooked: 0,
                  stock: 0, // No EventProduct stock available
                  bookings: []
                };
              }

              // Skip canceled orders
              if (equipment.orderLineItem.order.status === "canceled") {
                return;
              }

              const quantity = equipment.orderLineItem.qty || 0;

              // Check if equipment is from the main order or additional
              const isIncluded = equipment.orderLineItem.order.id === mainOrderId;
              if (isIncluded) {
                productSummary[productId].includedQuantity += quantity;
              } else {
                productSummary[productId].additionalQuantity += quantity;
              }

              productSummary[productId].totalBooked += quantity;

              // Add booking details
              productSummary[productId].bookings.push({
                companyId: booth.company?.id,
                companyName: booth.company?.name || '-',
                quantity,
                isIncluded,
                orderNumber: equipment.orderLineItem.order.orderNumber || '-'
              });
            }
          });
        });

        // Convert to array
        const summaryData = Object.values(productSummary);

        // Sort by product name
        summaryData.sort((a, b) => {
          const nameA = getLocalizedText(a.product.name);
          const nameB = getLocalizedText(b.product.name);
          return nameA.localeCompare(nameB);
        });

        setEquipmentSummary(summaryData);
      } catch (error) {
        console.error('Error fetching equipment data:', error);
      }
      setIsLoading(false);
    };

    fetchEquipmentData();
  }, [selectedEvent]);

  return (
    <MDBRow>
      <MDBCol>
        <MDBCard>
          <MDBCardBody>
            <MDBCardTitle>
              {t("Event Equipment Summary")}
              <MDBBtn
                className="ms-3"
                size="sm"
                outline
                color="primary" 
                onClick={handleDownloadExcel}
                disabled={equipmentSummary.length === 0}
              >
                <MDBIcon fas icon="file-excel" className="me-2" />
                {t("Download")}
              </MDBBtn>
            </MDBCardTitle>
            
            <MDBRow className="mb-4">
              <MDBCol md="4">
                <MDBSelect
                  label={t("Select Event")}
                  data={[
                    { text: t("Select an event..."), value: "" },
                    ...events.map(event => ({
                      text: getLocalizedText(event.name),
                      value: event.id
                    }))
                  ]}
                  value={selectedEvent || ""}
                  onChange={e => setSelectedEvent(e.value)}
                />
              </MDBCol>
            </MDBRow>

            {isLoading ? (
              <div className="text-center">
                <MDBSpinner />
              </div>
            ) : selectedEvent ? (
              <MDBTable>
                <MDBTableHead>
                  <tr>
                    <th>Kategorie</th>
                    <th>Ausstattung</th>
                    <th>Im Stand enthalten</th>
                    <th>Zusätzlich gebucht</th>
                    <th>Gesamt gebucht</th>
                    <th>Aktueller Bestand</th>
                  </tr>
                </MDBTableHead>
                <MDBTableBody>
                  {equipmentSummary.map((item, index) => (
                    <tr
                      key={index}
                      onClick={() => { setSelectedProduct(item); setShowModal(true); }}
                      style={{ cursor: 'pointer' }}
                      className="hover-shadow"
                    >
                      <td>{t(item.product.subType || item.product.type || '-')}</td>
                      <td>{getLocalizedText(item.product.name)}</td>
                      <td>{item.includedQuantity}</td>
                      <td>{item.additionalQuantity}</td>
                      <td>{item.totalBooked}</td>
                      <td>{item.stock}</td>
                    </tr>
                  ))}
                </MDBTableBody>
              </MDBTable>
            ) : (
              <div className="text-center text-muted">
                {t("Please select an event to view equipment summary")}
              </div>
            )}
          </MDBCardBody>
        </MDBCard>
      </MDBCol>

      {/* Product Details Modal */}
      <MDBModal open={showModal} onClose={() => setShowModal(false)} tabIndex="-1">
        <MDBModalDialog size="lg">
          <MDBModalContent>
            <MDBModalHeader>
              <MDBModalTitle>
                {selectedProduct && getLocalizedText(selectedProduct.product.name)}
              </MDBModalTitle>
              <MDBBtn className="btn-close" color="none" onClick={() => setShowModal(false)}></MDBBtn>
            </MDBModalHeader>
            <MDBModalBody>
              {selectedProduct && selectedProduct.bookings.length > 0 ? (
                <MDBTable small>
                  <MDBTableHead>
                    <tr>
                      <th>{t("Company")}</th>
                      <th>{t("Quantity")}</th>
                      <th>{t("Type")}</th>
                      <th>{t("Order Number")}</th>
                    </tr>
                  </MDBTableHead>
                  <MDBTableBody>
                    {selectedProduct.bookings.map((booking, index) => (
                      <tr key={index}>
                        <td>{booking.companyName}</td>
                        <td>{booking.quantity}</td>
                        <td>{booking.isIncluded ? t("Included in booth") : t("Additional booking")}</td>
                        <td>{booking.orderNumber}</td>
                      </tr>
                    ))}
                  </MDBTableBody>
                </MDBTable>
              ) : (
                <div className="text-center text-muted">
                  {t("No bookings for this product")}
                </div>
              )}
            </MDBModalBody>
          </MDBModalContent>
        </MDBModalDialog>
      </MDBModal>
    </MDBRow>
  );
};

export default EventEquipmentSummary;